import { Button, Menu, MenuItem } from '@mui/material'
import moment from 'moment'
import React, { useState } from 'react'
import { ExportIcon } from 'src/Libs/flow/icons'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { localizeEmobilityNumber } from './utils/localize-emobility-number'
import { exportToCsv, exportToExcel } from './Dashboard/Charts/file-export/export-file'
import { ChargingSessionExportCsvHeadersModel, ChargingSessionExportDataModel, ChargingSessionModel } from './charging-session-models'
import { getSessionStatusTranslation } from './charging-session-parser'
import { eMobilityPalette } from './palette'
import { getSessionsForExport } from './sessions-for-export'
import { formatDuration } from './utils/format-duration'

/**
 * Export charging sessions button component.
 * It contains export button and menu with menu items, handle click to open and handle close to close menu
 */
export const ChargingSessionsExportButton = (): JSX.Element => {
  const { t } = useCustomTranslation()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  return (
    <>
      <Button startIcon={<ExportIcon />} variant="text" color="secondary" disableElevation onClick={handleClick} sx={{ minWidth: 150, height: 54 }}>
        {t('emobility.chargingSession.export')}
      </Button>

      <ChargingSessionsExportMenu isOpen={isOpen} anchorEl={anchorEl} handleClose={handleClose} />
    </>
  )
}

interface GenerateCsvData {
  csvData: ChargingSessionExportDataModel[]
  csvHeaders: ChargingSessionExportCsvHeadersModel[]
}

const SEPARATOR = ','
const FILE_NAME = 'hager'

interface ChargingSessionsExportMenuProps {
  isOpen: boolean
  anchorEl: null | HTMLElement
  handleClose: () => void
}
/**
 * Export charging sessions menu component.
 * It contains menu with menu items and csv generator components, handler function for generate csv export file
 * @param anchorEl as null | HTMLElement for handling opened and closed HTML element
 * @param isOpen as boolean for handling if menu is closed or opened
 * @param handleClose as void function use to close menu
 */
const ChargingSessionsExportMenu: React.FC<ChargingSessionsExportMenuProps> = ({ isOpen, anchorEl, handleClose }): JSX.Element => {
  const sessions = getSessionsForExport()

  const { t, selectedAppLanguage } = useCustomTranslation()

  const { enqueueErrorStack } = useSnackbar()

  const generateChargingSessionsCsvData = (data: ChargingSessionModel[]): GenerateCsvData => {
    const csvData = data.map(session => {
      const totalSolarEnergy = (session.energySolar / 1000).toFixed(2)
      const totalEnergy = (session.energyAll / 1000).toFixed(2)
      let authStatus = t('emobility.chargingSession.authenticationInfoRfid', '', {
        rfid: session.authentication.data,
      })

      if (session.authentication.type === 'NONE') {
        authStatus = t('emobility.chargingSession.noAuthentication')
      }

      if (session.authentication.type === 'RFID' || session.authentication.type === 'rfid') {
        authStatus = session.authentication.cardName
          ? `${session.authentication.cardName} (${session.authentication.data})`
          : session.authentication.data
          ? t('emobility.chargingSession.authenticationInfoRfid', '', {
              rfid: session.authentication.data,
            })
          : t('emobility.chargingSession.noAuthentication')
      }

      return {
        startDate: moment(session.startAt).local().format(t('common.format.dateTime')),
        status: getSessionStatusTranslation(t, session.status),
        duration: formatDuration(session.durationMilliseconds, t),
        totalCharged: totalEnergy,
        mid: session.isMidCertified ? t('common.yes') : t('common.no'),
        solarCharged: totalSolarEnergy,
        solarRatio: t('common.format.percentageValue', '', {
          value: localizeEmobilityNumber(session.solarRatio),
        }),
        authentication: authStatus,
        chargingStationName: session.deviceName,
      }
    })

    const csvHeaderValues = Object.keys(csvData[0])
    const csvHeaders = csvHeaderValues.map(value => {
      return {
        label: `${t(`emobility.chargingSession.${mapHeaderValueToCsvHeaderLabel(value)}`)} ${
          value === 'totalCharged' || value === 'solarCharged' ? '(kWh)' : ''
        }`,
        key: value as keyof ChargingSessionModel,
      }
    })

    return { csvData, csvHeaders }
  }

  const handleExportXlsx = (): void => {
    if (!sessions.length) {
      enqueueErrorStack(t('shell.noData'))
      handleClose()
      return
    }

    const { csvHeaders, csvData } = generateChargingSessionsCsvData(sessions)
    const excelData = chargingSessionsToMatrix(csvHeaders, csvData)
    exportToExcel(excelData, FILE_NAME)

    handleClose()
  }

  const handleExportCsv = (): void => {
    if (!sessions.length) {
      enqueueErrorStack(t('shell.noData'))
      handleClose()
      return
    }

    const { csvHeaders, csvData } = generateChargingSessionsCsvData(sessions)
    const separator = selectedAppLanguage === 'de' ? ',' : SEPARATOR
    exportToCsv(csvHeaders, csvData, FILE_NAME, separator)

    handleClose()
  }

  const { selectedMenuItemHoverBackground } = eMobilityPalette

  return (
    <Menu slotProps={{ paper: { sx: { mt: 1.5, p: 0 } } }} anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
      <MenuItem
        sx={{
          minWidth: 150,
          fontSize: '12px',
          color: theme => theme.palette.grey[600],
          height: 54,
          '&.MuiMenuItem-root:hover': {
            bgcolor: selectedMenuItemHoverBackground,
          },
        }}
        onClick={handleExportXlsx}
      >
        {t('emobility.chargingSession.excelExport')}
      </MenuItem>
      <MenuItem
        sx={{
          minWidth: 150,
          fontSize: '12px',
          color: theme => theme.palette.grey[600],
          height: 54,
          '&.MuiMenuItem-root:hover': {
            bgcolor: selectedMenuItemHoverBackground,
          },
        }}
        onClick={handleExportCsv}
      >
        {t('emobility.chargingSession.csvExport')}
      </MenuItem>
    </Menu>
  )
}

/**
 * Utility function needed for mapping the csvHeader labels to the appropriate translations
 * in order to avoid duplicate translations with different translation keys
 * @param {string} csvHeaderLabelKey - The key from the csvHeader labels
 * @returns string
 */
function mapHeaderValueToCsvHeaderLabel(csvHeaderLabelKey: string): string {
  switch (csvHeaderLabelKey) {
    case 'totalCharged':
      return 'totalEnergy'
    case 'mid':
      return 'mid-certified-label'
    case 'solarCharged':
      return 'solarEnergy'
    case 'chargingStationName':
      return 'chargingStation'
    case 'badgeName':
      return 'authentication'
    default:
      return csvHeaderLabelKey
  }
}

/** Map charging session data to array of arrays. Used to save session data in xlsx file. */
const chargingSessionsToMatrix = (
  csvHeaders: ChargingSessionExportCsvHeadersModel[],
  csvData: ChargingSessionExportDataModel[]
): (string | number)[][] => {
  const keys = csvHeaders.map(({ key }) => key)
  const headers = csvHeaders.map(({ label }) => label)

  const rows = csvData.map(datum => {
    return keys.map(key => {
      const result = datum[key as keyof ChargingSessionExportDataModel] ?? ''
      return NUMBER_REGEX.test(result) ? parseFloat(result) : result
    })
  })

  return [headers, ...rows]
}

const NUMBER_REGEX = /^[0-9]+(\.|,)[0-9]+$|^[0-9]+$/
